import type { SupportedCurrency } from '@plevits/types';
import { COUNTRY_CURRENCY_MAP, DEFAULT_CURRENCY, VALID_CURRENCIES } from './currency-config';

const CURRENCY_STORAGE_KEY = 'plevits_preferred_currency';

export function detectCurrencyFromLocale(): SupportedCurrency {
  try {
    const locale = navigator.language || navigator.languages?.[0] || '';
    const countryCode = locale.split('-')[1]?.toUpperCase();
    if (countryCode && COUNTRY_CURRENCY_MAP[countryCode]) {
      return COUNTRY_CURRENCY_MAP[countryCode];
    }
  } catch {
    // Fallback silently
  }
  return DEFAULT_CURRENCY;
}

export function getSavedCurrency(): SupportedCurrency | null {
  try {
    const saved = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (saved && VALID_CURRENCIES.includes(saved as SupportedCurrency)) {
      return saved as SupportedCurrency;
    }
  } catch {
    // localStorage may be unavailable
  }
  return null;
}

export function saveCurrency(currency: SupportedCurrency): void {
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  } catch {
    // localStorage may be unavailable
  }
}

/**
 * Resolve the effective currency with priority:
 * - billingCurrency (from GET /billing/status) > serverPreference (from GET /auth/me) > localStorage > locale hint > USD
 */
export function getEffectiveCurrency(billingCurrency?: SupportedCurrency | null, serverPreference?: SupportedCurrency | null): SupportedCurrency {
  if (billingCurrency && VALID_CURRENCIES.includes(billingCurrency)) {
    return billingCurrency;
  }
  if (serverPreference && VALID_CURRENCIES.includes(serverPreference)) {
    return serverPreference;
  }
  return getSavedCurrency() ?? detectCurrencyFromLocale();
}
